// MOMERY CUSTOM: Habitat 14 variant conflict resolution and Printess protection.
(function (root) {
  'use strict';

  const PRINTESS_BUTTON_SELECTOR = '.printess-integration-button';
  const VARIANT_DATA_SELECTOR = 'script[data-momery-variant-data], script[type="application/json"]';
  let installed = false;

  function dimensionsOf(value) {
    const match = String(value || '').match(
      /(\d+(?:[.,]\d+)?)\s*(?:x|×)\s*(\d+(?:[.,]\d+)?)/i
    );
    if (!match) return null;

    return [
      Number.parseFloat(match[1].replace(',', '.')),
      Number.parseFloat(match[2].replace(',', '.')),
    ];
  }

  function hasMirroredDimensions(currentValue, candidateValue) {
    const current = dimensionsOf(currentValue);
    const candidate = dimensionsOf(candidateValue);
    if (!current || !candidate) return false;

    return current[0] === candidate[1] && current[1] === candidate[0];
  }

  function preferredDownstreamValue(candidates, optionIndex, currentValue) {
    if (candidates.some((variant) => variant.options[optionIndex] === currentValue)) {
      return currentValue;
    }

    // MOMERY CUSTOM: preserve the format when orientation swaps width and height.
    const mirroredVariant = candidates.find((variant) => {
      return hasMirroredDimensions(currentValue, variant.options[optionIndex]);
    });

    return mirroredVariant?.options[optionIndex] ?? candidates[0].options[optionIndex];
  }

  function resolveDownstreamValues(variants, selectedValues, changedOptionIndex) {
    let candidates = variants.filter((variant) => {
      if (variant.available !== true) return false;

      for (let index = 0; index <= changedOptionIndex; index += 1) {
        if (variant.options[index] !== selectedValues[index]) return false;
      }
      return true;
    });

    if (!candidates.length) return selectedValues.slice();

    const resolved = selectedValues.slice();
    for (let index = changedOptionIndex + 1; index < resolved.length; index += 1) {
      const nextValue = preferredDownstreamValue(candidates, index, resolved[index]);

      resolved[index] = nextValue;
      candidates = candidates.filter((variant) => variant.options[index] === nextValue);
    }

    return resolved;
  }

  function getFieldsets(variantSelects) {
    return Array.from(variantSelects.querySelectorAll('fieldset'));
  }

  function getFieldsetValue(fieldset) {
    const select = fieldset.querySelector('select');
    if (select) return select.value;

    return fieldset.querySelector('input:checked')?.value || '';
  }

  function setFieldsetValue(fieldset, value) {
    const select = fieldset.querySelector('select');
    if (select) {
      select.value = value;
      return select.value === value;
    }

    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const selectedInput = inputs.find((input) => input.value === value);
    if (!selectedInput) return false;

    inputs.forEach((input) => {
      input.checked = input === selectedInput;
    });
    return true;
  }

  function getVariantData(variantSelects) {
    if (variantSelects._momeryVariantData) return variantSelects._momeryVariantData;

    const data = variantSelects.querySelector(VARIANT_DATA_SELECTOR);
    if (!data) return [];

    try {
      variantSelects._momeryVariantData = JSON.parse(data.textContent || '[]');
    } catch (error) {
      console.error('[MOMERY_VARIANTS] Invalid variant data', error);
      variantSelects._momeryVariantData = [];
    }
    return variantSelects._momeryVariantData;
  }

  function resolveVariantSelection(variantSelects, changedControl) {
    const fieldsets = getFieldsets(variantSelects);
    const changedFieldset = changedControl?.closest?.('fieldset');
    const changedOptionIndex = fieldsets.indexOf(changedFieldset);
    if (changedOptionIndex < 0) return false;

    const selectedValues = fieldsets.map(getFieldsetValue);
    const resolvedValues = resolveDownstreamValues(
      getVariantData(variantSelects),
      selectedValues,
      changedOptionIndex
    );

    let didChange = false;
    for (let index = changedOptionIndex + 1; index < resolvedValues.length; index += 1) {
      if (resolvedValues[index] === selectedValues[index]) continue;
      if (setFieldsetValue(fieldsets[index], resolvedValues[index])) didChange = true;
    }
    return didChange;
  }

  function getPrintessVariantSelects(button) {
    const productWrapper = button.closest?.('.thb-product-detail');
    if (productWrapper) return productWrapper.querySelector('variant-selects');

    const productForm = button.closest?.('form[data-type="add-to-cart-form"]');
    if (!productForm || typeof document === 'undefined') return null;

    return Array.from(document.querySelectorAll('variant-selects')).find((variantSelects) => {
      return `product-form-${variantSelects.dataset.section}` === productForm.id;
    }) || null;
  }

  function printessVariantIsReady(variantSelects) {
    const currentVariant = variantSelects?.currentVariant;
    if (!currentVariant || currentVariant.available !== true || typeof document === 'undefined') return false;

    const productForm = document.getElementById(`product-form-${variantSelects.dataset.section}`);
    const variantInput = productForm?.querySelector('[name="id"]');

    return String(variantInput?.value || '') === String(currentVariant.id);
  }

  function syncPrintessButton(button) {
    const variantSelects = getPrintessVariantSelects(button);

    // MOMERY CUSTOM: Products with only Shopify's default variant intentionally
    // render no variant picker. In that case Printess owns the button state and
    // there is no variant conflict for this guard to resolve.
    if (!variantSelects) {
      button.classList.toggle('is-variant-unavailable', false);
      if (button.dataset.variantGuardBlocked === 'true') {
        button.removeAttribute('aria-disabled');
        delete button.dataset.variantGuardBlocked;
      }
      if (button.dataset.variantGuardDisabled === 'true' && button.dataset.printessStatus !== 'loading') {
        button.disabled = false;
        delete button.dataset.variantGuardDisabled;
      }
      return;
    }

    const isBlocked = !printessVariantIsReady(variantSelects);

    button.classList.toggle('is-variant-unavailable', isBlocked);
    if (isBlocked) {
      if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
      button.dataset.variantGuardBlocked = 'true';
      if (!button.disabled) {
        button.disabled = true;
        button.dataset.variantGuardDisabled = 'true';
      }
      return;
    }

    button.removeAttribute('aria-disabled');
    delete button.dataset.variantGuardBlocked;
    if (button.dataset.variantGuardDisabled === 'true' && button.dataset.printessStatus !== 'loading') {
      button.disabled = false;
      delete button.dataset.variantGuardDisabled;
    }
  }

  function syncPrintessButtons(scope) {
    const rootScope = scope || document;
    rootScope.querySelectorAll(PRINTESS_BUTTON_SELECTOR).forEach(syncPrintessButton);
  }

  function install() {
    if (installed || typeof document === 'undefined') return;
    installed = true;

    document.addEventListener('change', (event) => {
      const variantSelects = event.target?.closest?.('variant-selects');
      if (variantSelects) resolveVariantSelection(variantSelects, event.target);

      if (event.target?.matches?.('[name="id"]')) syncPrintessButtons();
    }, true);

    document.addEventListener('click', (event) => {
      const button = event.target?.closest?.(PRINTESS_BUTTON_SELECTOR);
      if (!button) return;

      const variantSelects = getPrintessVariantSelects(button);
      if (!variantSelects || printessVariantIsReady(variantSelects)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      syncPrintessButton(button);
    }, true);

    if (typeof MutationObserver !== 'undefined' && document.body) {
      new MutationObserver(() => syncPrintessButtons()).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'data-printess-status']
      });
    }

    syncPrintessButtons();
  }

  const api = {
    dimensionsOf,
    hasMirroredDimensions,
    install,
    preferredDownstreamValue,
    printessVariantIsReady,
    resolveDownstreamValues,
    resolveVariantSelection,
    syncPrintessButton,
    syncPrintessButtons
  };

  root.MomeryProductVariantGuard = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (typeof document !== 'undefined') install();
})(typeof window !== 'undefined' ? window : globalThis);
